// Elenco ragionato dei token del design system: l'unica descrizione leggibile di `design-tokens.css`.
// La vetrina `/styleguide` lo disegna e `test/design-system.test.ts` verifica che coincida col CSS
// (ogni token del CSS è qui, ogni token qui è nel CSS). Per aggiungerne uno: prima il CSS, poi questa riga.

export type TokenPreview = "color" | "text" | "space" | "radius" | "shadow" | "size" | "layer" | "font" | "weight";

export type Token = { readonly name: `--${string}`; readonly use: string };

export type TokenGroup = {
  readonly id: string;
  readonly title: string;
  readonly preview: TokenPreview;
  readonly tokens: readonly Token[];
};

export const tokenGroups: readonly TokenGroup[] = [
  {
    id: "colors-surface",
    title: "Colori · fondi e bordi",
    preview: "color",
    tokens: [
      { name: "--color-bg", use: "fondo della pagina" },
      { name: "--color-surface", use: "schede, tabelle, finestre" },
      { name: "--color-surface-raised", use: "righe d'intestazione, hover" },
      { name: "--color-border", use: "bordo sottile" },
      { name: "--color-border-strong", use: "bordo dei controlli" },
    ],
  },
  {
    id: "colors-text",
    title: "Colori · testo",
    preview: "color",
    tokens: [
      { name: "--color-text", use: "testo principale" },
      { name: "--color-text-muted", use: "testo secondario, etichette" },
      { name: "--color-text-inverse", use: "testo su fondo d'azione" },
    ],
  },
  {
    id: "colors-accent",
    title: "Colori · azione",
    preview: "color",
    tokens: [
      { name: "--color-accent", use: "l'unico colore d'azione: pulsante primario, link, selezione" },
      { name: "--color-accent-strong", use: "azione al passaggio del mouse" },
      { name: "--color-accent-soft", use: "fondo tenue di una voce attiva" },
    ],
  },
  {
    id: "colors-status",
    title: "Colori · stati",
    preview: "color",
    tokens: [
      { name: "--color-success", use: "riuscito (sempre con testo e icona)" },
      { name: "--color-warning", use: "attenzione o scadenza" },
      { name: "--color-danger", use: "errore" },
      { name: "--color-info", use: "informazione" },
      { name: "--tint-success", use: "fondo tenue del successo" },
      { name: "--tint-warning", use: "fondo tenue dell'attenzione" },
      { name: "--tint-danger", use: "fondo tenue dell'errore" },
      { name: "--tint-info", use: "fondo tenue dell'informazione" },
      { name: "--tint-neutral", use: "fondo tenue neutro (chip, pillole)" },
    ],
  },
  {
    id: "fonts",
    title: "Caratteri",
    preview: "font",
    tokens: [
      { name: "--font-sans", use: "tutto il testo (Plus Jakarta Sans, servito dall'app)" },
      { name: "--font-mono", use: "codici, nomi tecnici" },
    ],
  },
  {
    id: "text-sizes",
    title: "Scritte · dimensioni",
    preview: "text",
    tokens: [
      { name: "--text-xs", use: "12 px · minimo operativo, note" },
      { name: "--text-sm", use: "13 px · etichette, tabelle dense" },
      { name: "--text-md", use: "14 px · testo di base" },
      { name: "--text-lg", use: "16 px · titoli di sezione" },
      { name: "--text-xl", use: "20 px · titoli minori" },
      { name: "--text-2xl", use: "26 px · numeri grandi" },
      { name: "--text-3xl", use: "28 px · titolo di pagina" },
      { name: "--leading-none", use: "interlinea di una riga sola (icone, numeri)" },
      { name: "--leading-snug", use: "interlinea dei titoli grandi" },
      { name: "--leading-tight", use: "interlinea dei titoli" },
      { name: "--leading-normal", use: "interlinea del testo" },
      { name: "--leading-relaxed", use: "interlinea dei campi su più righe" },
    ],
  },
  {
    id: "text-weights",
    title: "Scritte · pesi",
    preview: "weight",
    tokens: [
      { name: "--weight-regular", use: "400 · testo corrente" },
      { name: "--weight-medium", use: "500 · testo leggermente marcato" },
      { name: "--weight-semibold", use: "600 · etichette, intestazioni di colonna" },
      { name: "--weight-bold", use: "700 · titoli, numeri in evidenza" },
      { name: "--weight-extrabold", use: "800 · cifre e sigle da risaltare" },
    ],
  },
  {
    id: "space",
    title: "Spazi",
    preview: "space",
    tokens: [
      { name: "--space-1", use: "4 px" },
      { name: "--space-2", use: "8 px" },
      { name: "--space-3", use: "12 px" },
      { name: "--space-4", use: "16 px" },
      { name: "--space-5", use: "20 px" },
      { name: "--space-6", use: "24 px" },
      { name: "--space-8", use: "32 px" },
      { name: "--space-10", use: "40 px" },
    ],
  },
  {
    id: "radius",
    title: "Angoli",
    preview: "radius",
    tokens: [
      { name: "--radius-xs", use: "barre e quadratini dei grafici" },
      { name: "--radius-sm", use: "controlli e schede" },
      { name: "--radius-md", use: "finestre e pannelli" },
      { name: "--radius-pill", use: "pillole e chip" },
    ],
  },
  {
    id: "elevation",
    title: "Ombre e focus",
    preview: "shadow",
    tokens: [
      { name: "--shadow-sm", use: "nessuna ombra: il tema è piatto" },
      { name: "--shadow-md", use: "finestre e menu sopra la pagina" },
      { name: "--focus-ring", use: "anello del focus da tastiera, uguale ovunque" },
    ],
  },
  {
    id: "markers",
    title: "Segni a riga",
    preview: "shadow",
    tokens: [
      { name: "--shadow-marker-start", use: "riga scelta: segno d'accento a sinistra (azienda aperta); trascinamento a sinistra" },
      { name: "--shadow-marker-end", use: "trascinamento: segno d'accento a destra" },
      { name: "--shadow-marker-start-danger", use: "riga scaduta: segno rosso a sinistra (con la parola, mai il solo colore)" },
      { name: "--shadow-marker-top", use: "trascinamento in un elenco: cade sopra" },
      { name: "--shadow-marker-bottom", use: "trascinamento in un elenco: cade sotto" },
      { name: "--shadow-outline", use: "contorno sottile dentro un elemento, senza bordo" },
      { name: "--shadow-outline-accent", use: "contorno d'accento della scelta fatta (filtri a pulsante)" },
      { name: "--shadow-sticky-start", use: "bordo e ombra della colonna ferma a sinistra di una tabella" },
      { name: "--shadow-sticky-end", use: "bordo e ombra della colonna ferma a destra di una tabella" },
      { name: "--shadow-marker-start-danger-sticky", use: "riga in errore nella colonna ferma: segno rosso e bordo" },
      { name: "--shadow-gap-inline", use: "uno spazio del colore del fondo ai due lati (separa una barra dalle vicine)" },
      { name: "--shadow-ring-soft", use: "anello tenue d'accento attorno a un elemento vivo (la chiamata che squilla)" },
    ],
  },
  {
    id: "sizes",
    title: "Dimensioni dei controlli",
    preview: "size",
    tokens: [
      { name: "--control-height", use: "36 px · controlli su desktop" },
      { name: "--control-height-sm", use: "28 px · controlli compatti" },
      { name: "--control-height-touch", use: "44 px · minimo su touch" },
      { name: "--content-width-narrow", use: "pagine strette (accesso)" },
      { name: "--content-width-wide", use: "pagine larghe" },
    ],
  },
  {
    id: "layers",
    title: "Livelli (z-index)",
    preview: "layer",
    tokens: [
      { name: "--z-raised", use: "sopra i vicini" },
      { name: "--z-sticky", use: "righe e colonne ferme" },
      { name: "--z-sticky-corner", use: "incrocio fermo, barra «Salva»" },
      { name: "--z-floating", use: "avvisi fermi, menu a tendina" },
      { name: "--z-topbar", use: "barra in alto sotto 720 px" },
      { name: "--z-rail", use: "menu laterale" },
      { name: "--z-backdrop", use: "velo dietro il menu mobile" },
      { name: "--z-rail-open", use: "menu mobile aperto" },
      { name: "--z-skip", use: "«Vai al contenuto»" },
      { name: "--z-toast", use: "avvisi di conferma, in cima a tutto" },
    ],
  },
];

// I punti di rottura non possono essere token CSS (`@media` non legge `var()`): stanno qui, e il test
// controlla che nessun `@media (max/min-width)` usi un valore diverso.
export const breakpoints = [
  { px: 640, use: "tabelle a scheda dei report più strette" },
  { px: 719, use: "coppia di 720 per `max-width` esclusivo" },
  { px: 720, use: "telefono: menu in un drawer, pannelli a tutta altezza" },
  { px: 721, use: "coppia di 720 per `min-width` esclusivo" },
  { px: 761, use: "tabella dei Task a righe" },
  { px: 860, use: "tabelle dei report a schede" },
  { px: 980, use: "tablet: impostazioni e vendite in colonna" },
] as const;

export const allTokenNames: readonly string[] = tokenGroups.flatMap((group) => group.tokens.map((token) => token.name));
